import { ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { ShopProductParameter } from '@dto';
import { UrlTranslateService } from "@service/helpers/url-translate.service";
import { GpsrService } from "@service/gpsr/gpsr.service";
import { GpsrView, selectGpsrView } from "app/model/gpsr.model";
import { GpsrPopupComponent } from "../gpsr-popup/gpsr-popup.component";

/** Wiersz GPSR w tabeli specyfikacji: etykieta roli + nazwa podmiotu jako odnośnik. */
export interface GpsrRow
{
  etykieta: string;
  nazwa: string;
}

@Component({
  selector: 'app-product-parameters',
  templateUrl: './product-parameters.component.html',
  styleUrls: ['./product-parameters.component.scss']
})
export class ProductParametersComponent implements OnChanges
{

  public isEven: string = 'even';

  /**
   * Dane GPSR (#1710). `null` znaczy „nie ma czego pokazać" — wtedy nie powstaje
   * żaden wiersz. To jedyny dopuszczalny stan pusty: nigdy pusty wiersz, nigdy
   * wiersz z samą etykietą.
   */
  public gpsrView: GpsrView = null;

  /**
   * Wierszy bywa jeden albo dwa: art. 19 GPSR wymaga producenta zawsze, a przy
   * producencie spoza UE DODATKOWO osoby odpowiedzialnej w UE. Nazwy obu ról
   * zostają widoczne wprost w tabeli; za kliknięciem chowają się dopiero adresy
   * i kontakty.
   */
  public gpsrRows: GpsrRow[] = [];

  @ViewChild(GpsrPopupComponent) private gpsrPopup: GpsrPopupComponent;

  constructor(
    private router: Router,
    private sanitizer: DomSanitizer,
    private changeDet: ChangeDetectorRef,
    public urlTranslateService: UrlTranslateService,
    private gpsrService: GpsrService,
  )
  {
  }

  private _productParameters: ShopProductParameter[];
  @Input() public set productParameters(value: ShopProductParameter[])
  {
    this._productParameters = value;
    this.recalculateParity();
  }
  public get productParameters(): ShopProductParameter[]
  {
    return this._productParameters;
  }

  private _familyCode: string;
  @Input() public set familyCode(value: string)
  {
    if (!value || value === this._familyCode)
    {
      return;
    }

    this._familyCode = value;
    this.loadGpsr(value);
  }
  public get familyCode(): string
  {
    return this._familyCode;
  }

  public sanitize(url: string): SafeUrl
  {
    return this.sanitizer.bypassSecurityTrustUrl(url);
  }

  public ngOnChanges(changes: SimpleChanges): void
  {
    this.changeDet.detectChanges();
  }

  public goToListing(url): void
  {
    this.router.navigateByUrl(url);
  }

  public openGpsrPopup(): void
  {
    if (this.gpsrPopup)
    {
      this.gpsrPopup.openModal();
    }
  }

  private loadGpsr(familyCode: string): void
  {
    this.gpsrService.getByFamilyCode(familyCode).subscribe((response) =>
    {
      this.gpsrView = selectGpsrView(response);
      this.gpsrRows = this.buildRows(this.gpsrView);
      // Wiersze GPSR dokładają się do listy parametrów, więc zmieniają parzystość,
      // od której zależy podział tabeli na dwie kolumny. Bez przeliczenia siatka
      // rozjeżdża się dokładnie w chwili, gdy dane dojdą.
      this.recalculateParity();
      this.changeDet.detectChanges();
    });
  }

  private buildRows(view: GpsrView): GpsrRow[]
  {
    if (!view)
    {
      return [];
    }

    const rows: GpsrRow[] = [];

    if (view.manufacturer)
    {
      // Etykieta „Podmiot odpowiedzialny" także dla producenta — decyzja biznesowa
      // (2026-08-26): w tabeli specyfikacji obie role występują pod wspólną nazwą,
      // rozróżnienie ról zostaje w modalu.
      rows.push({ etykieta: 'Podmiot odpowiedzialny', nazwa: view.manufacturer.name });
    }

    if (view.euResponsible)
    {
      // Nazewnictwo z projektu — ta sama etykieta co nagłówek sekcji w modalu,
      // żeby wiersz w tabeli i modal mówiły o tej roli tak samo.
      rows.push({ etykieta: 'Podmiot odpowiedzialny', nazwa: view.euResponsible.name });
    }

    return rows;
  }

  private recalculateParity(): void
  {
    const parametersCount = this._productParameters ? this._productParameters.length : 0;
    const rowsCount = parametersCount + this.gpsrRows.length;

    this.isEven = rowsCount % 2 === 0 ? 'even' : 'odd';
  }
}
